import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoginRequest } from '../types/api';
import { authService } from '../services/auth-service';
import { isTwoFactorChallenge } from '../services/twofactor-service';
import { useAuthStore } from '../stores/auth-store';
import { httpClient } from '../services/http-client';
import { AuthHeader } from '../components/AuthHeader';

interface DemoPageProps {
  onLoginSuccess?: () => void;
}

const DEMO_ACCOUNTS = [
  {
    email: 'demo@folioworth.com',
    password: 'Passw0rd!',
    description: 'Primary demo account (Household Owner)',
  },
  {
    email: 'demo2@folioworth.com',
    password: 'Passw0rd2!',
    description: 'Secondary demo account (Household Member)',
  },
];

export const DemoPage: React.FC<DemoPageProps> = ({
  onLoginSuccess,
}) => {
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });

  const { login, setLoading, setError, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const handleSelectDemo = (email: string, password: string) => {
    setFormData({ email, password });
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(formData);

      // The demo accounts are seeded without a second factor, so a challenge
      // here means something is misconfigured. Say so rather than silently
      // treating an empty response as a session.
      if (isTwoFactorChallenge(response.data)) {
        setError('This demo account needs a verification code. Sign in from the normal sign-in page.');
        return;
      }

      // Set the auth token in the HTTP client
      httpClient.setAuthToken(response.data.access_token);

      // Update auth store
      login(response.data.user, response.data.access_token);

      onLoginSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <AuthHeader />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h2" variant="h5" align="center" gutterBottom>
              View Demo
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
              Select a demo account to explore the application
            </Typography>

          {/* Demo Account Cards */}
          <Box sx={{ mb: 3 }}>
            {DEMO_ACCOUNTS.map((account, index) => (
              <Card
                key={account.email}
                sx={{
                  mb: 2,
                  border: formData.email === account.email ? 2 : 1,
                  borderColor: formData.email === account.email ? 'primary.main' : 'divider',
                }}
              >
                <CardActionArea
                  onClick={() => handleSelectDemo(account.email, account.password)}
                  disabled={isLoading}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Demo Account {index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {account.description}
                    </Typography>
                    <Box sx={{
                      bgcolor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      border: 1,
                      borderColor: 'divider'
                    }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Email:</strong> {account.email}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Password:</strong> {account.password}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || !formData.email || !formData.password}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In to Demo'
              )}
            </Button>

          </Box>
        </Paper>
      </Box>
    </Container>
    </Box>
  );
};
